/**
 * Team API client for GhostFlow RBAC system.
 * Handles team management, member invitations, and role management.
 */

import { api } from './client'

// =============================================================================
// Types matching backend proto definitions
// =============================================================================

export type TeamRole = 'owner' | 'admin' | 'manager' | 'member' | 'viewer'
export type TeamStatus = 'active' | 'suspended' | 'deleted'
export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'cancelled'

export interface Team {
  id: string
  ownerId: string
  name: string
  slug: string
  plan: string
  status: TeamStatus
  limits: Record<string, number>
  settings: string
  createdAt: string
  updatedAt: string
}

/** Extended team interface returned by listTeams — includes the current user's role. */
export interface TeamWithRole extends Team {
  /** The requesting user's role in this team. */
  myRole: TeamRole
  /** Whether this is the user's default team. */
  isDefault: boolean
  /** Number of members in this team. */
  memberCount: number
}

export interface TeamMember {
  userId: string
  teamId: string
  role: TeamRole
  isDefault: boolean
  userEmail: string
  userName: string
  joinedAt: string
  /** Whether the user has a custom (non-default) access configuration. */
  accessConfigured?: boolean
}

export interface TeamInvitation {
  id: string
  teamId: string
  email: string
  role: TeamRole
  invitedBy: string
  invitedByName?: string
  status: InvitationStatus
  expiresAt: string
  createdAt: string
}

// =============================================================================
// Request/Response types
// =============================================================================

export interface CreateTeamRequest {
  name: string
}

export interface UpdateTeamRequest {
  name: string
}

export interface InviteMemberRequest {
  email: string
  role: TeamRole
}

export interface UpdateMemberRoleRequest {
  userId: string
  role: TeamRole
}

export interface AcceptInvitationResponse {
  team: Team
  member: TeamMember
}

export interface ListMembersResponse {
  members: TeamMember[]
  pendingInvitations: TeamInvitation[]
}

// =============================================================================
// API Functions
// =============================================================================

export const teamsApi = {
  // --- Team CRUD ---

  /**
   * Create a new team.
   */
  createTeam: (data: CreateTeamRequest) =>
    api.post<Team>('/v1/teams', data),

  /**
   * Get team by ID.
   */
  getTeam: (teamId: string) =>
    api.get<Team>(`/v1/teams/${teamId}`),

  /**
   * List all teams for the current user (with role and member count).
   */
  listTeams: () =>
    api.get<TeamWithRole[]>('/v1/teams'),

  /**
   * Update team settings.
   */
  updateTeam: (teamId: string, data: UpdateTeamRequest) =>
    api.patch<Team>(`/v1/teams/${teamId}`, data),

  /**
   * Delete a team (owner only).
   */
  deleteTeam: (teamId: string) =>
    api.delete<{ success: boolean }>(`/v1/teams/${teamId}`),

  // --- Member Management ---

  /**
   * List all members and pending invitations for a team.
   */
  listMembers: (teamId: string) =>
    api.get<ListMembersResponse>(`/v1/teams/${teamId}/members`),

  /**
   * Update a member's role.
   */
  updateMemberRole: (teamId: string, userId: string, role: TeamRole) =>
    api.patch<TeamMember>(`/v1/teams/${teamId}/members/${userId}`, { role }),

  /**
   * Remove a member from the team.
   */
  removeMember: (teamId: string, userId: string) =>
    api.delete<{ success: boolean }>(`/v1/teams/${teamId}/members/${userId}`),

  // --- Invitations ---

  /**
   * Send an invitation to join the team.
   */
  inviteMember: (teamId: string, data: InviteMemberRequest) =>
    api.post<TeamInvitation>(`/v1/teams/${teamId}/invitations`, data),

  /**
   * Accept an invitation using the token from email.
   */
  acceptInvitation: (token: string) =>
    api.post<AcceptInvitationResponse>('/v1/teams/invitations/accept', { token }),

  /**
   * Cancel a pending invitation.
   */
  cancelInvitation: (teamId: string, invitationId: string) =>
    api.delete<{ success: boolean }>(`/v1/teams/${teamId}/invitations/${invitationId}`),

  /**
   * Resend an invitation email.
   */
  resendInvitation: (teamId: string, invitationId: string) =>
    api.post<TeamInvitation>(`/v1/teams/${teamId}/invitations/${invitationId}/resend`),

  // --- Team Context ---

  /**
   * Switch the user's active team context.
   */
  switchTeam: (teamId: string) =>
    api.post<Team>(`/v1/teams/${teamId}/switch`),
}
